import { Injectable } from "@angular/core";
import { duration } from "moment";
import { ICharacteristicValue } from "rl-common/models/i-characteristic-value";
import { ICharacteristicValueGroup } from "rl-common/models/i-characteristic-value-group";
import { ICharacteristicValueGroupCharacteristicValueAssociation } from "rl-common/models/i-characteristic-value-group-characteristic-value-association";
import { ICharacteristicValueHierarchy } from "rl-common/models/i-characteristic-value-hierarchy";
import { ICharacteristicValueHierarchyValueGroupAssociation } from "rl-common/models/i-characteristic-value-hierarchy-value-group-association";
import { ICharacteristicValueSet } from "rl-common/models/i-characteristic-value-set";
import { ICharacteristicValueSource } from "rl-common/models/i-characteristic-value-source";
import { RLHttpCache, RlHttpClient } from "rl-common/services/rl-http-client.service";
import { ISearchRequestModel } from "rl-common/services/search/models/search-request.model";
import { Observable } from "rxjs";
import { IExportLovHierarchyModel, IPicklistResponse, PicklistSourceEditMetaData, PicklistSourceUsedByRef, PicklistUsedByRef } from "./picklist-config.models";

@Injectable({
	providedIn: "root"
})
export class PicklistConfigService {
	private _baseUrl = "/picklist-config";

	constructor(private readonly _httpClient: RlHttpClient,
		private readonly _httpCache: RLHttpCache
	) {

	}

	getPicklistKey(picklistSourceId: string, prefix: string = "picklist_") {
		return prefix + picklistSourceId;
	}

	getPicklistMetaDataKey(picklistSourceId: string) {
		return this.getPicklistKey(picklistSourceId, "picklist_metadata_")
	}

	getPicklists() {
		return this._httpClient.get(`${this._baseUrl}/picklists`)
			.fetch<{
				picklists: ICharacteristicValueSource[];
				picklistSources: ICharacteristicValueSet[];
				presets: ICharacteristicValueGroup[];
				usedPicklistIds: string[];
				usedByChars: PicklistUsedByRef[];
			}>();
	}

	getPicklistSources() {
		return this._httpClient.get(`${this._baseUrl}/sources`)
			.fetch<{
				picklistSources: ICharacteristicValueSet[];
				usedByPicklists: _.Dictionary<ICharacteristicValueSource[]>;
				usedByCmds: _.Dictionary<PicklistSourceUsedByRef[]>;
			}>();
	}

	getPicklistSourceUsedInPicklists(picklistSourceId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${picklistSourceId}/picklists`)
			.fetch<ICharacteristicValueSource[]>();
	}

	getPicklistSourceUsedInChars(picklistSourceId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${picklistSourceId}/chars`)
			.fetch<PicklistSourceUsedByRef[]>();
	}

	getPicklistSourceValues(picklistSourceId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${picklistSourceId}/values`)
			.cache(this.getPicklistKey(picklistSourceId), duration(5, "minute"))
			.fetch<ICharacteristicValue[]>();
	}

	getPicklistSourceEditMetaData(picklistSourceId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${picklistSourceId}/edit/metadata`)
			.cache(this.getPicklistKey(picklistSourceId, "picklist_metadata_"), duration(5, "minute"))
			.fetch<PicklistSourceEditMetaData>();
	}

	createPicklistSource(picklistSource: ICharacteristicValueSet, charValues: ICharacteristicValue[]) {
		return this._httpClient.post(`${this._baseUrl}/sources`)
			.body({
				picklistSource,
				charValues
			})
			.fetch<string>();
	}

	updatePicklistSource(picklistSource: ICharacteristicValueSet, charValues: ICharacteristicValue[], deletedCharValueIds: number[], deletedEntityIds: string[]) {
		this._httpCache.remove(this.getPicklistKey(picklistSource.charValueSetID));
		this._httpCache.remove(this.getPicklistMetaDataKey(picklistSource.charValueSetID));
		return this._httpClient.put(`${this._baseUrl}/sources/${picklistSource.charValueSetID}`)
			.body({
				picklistSource,
				charValues,
				deletedCharValueIds,
				deletedEntityIds
			})
			.fetch<string>();
	}

	getCharValueCanDeleteFromPicklistSource(charValueSetId: string, charValueId: number) {
		return this._httpClient.get(`${this._baseUrl}/sources/${charValueSetId}/char-value-can-delete/${charValueId}`)
			.fetch<boolean>();
	}

	getPicklistSourceCount(charValueSetId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${charValueSetId}/count`)
			.fetch<number>();
	}

	deletePicklistSource(charValueSetId: string) {
		return this._httpClient
			.delete(`${this._baseUrl}/sources/${charValueSetId}/delete`)
			.fetch<void>();
	}

	getPicklistHierarchies(picklistId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/hierarchies`)
			.fetch<ICharacteristicValueHierarchy[]>();
	}

	getPicklistPresetAssocs(picklistId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/preset-assocs`)
			.fetch<ICharacteristicValueHierarchyValueGroupAssociation[]>();
	}

	getPicklistPresets(picklistId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/presets`)
			.fetch<ICharacteristicValueGroup[]>();
	}

	getPicklistPresetValues(picklistId: string, presetId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/presets/${presetId}/values`)
			.fetch<ICharacteristicValueGroupCharacteristicValueAssociation[]>();
	}

	createPicklist(picklist: ICharacteristicValueSource, charValues: ICharacteristicValue[], hierarchies: ICharacteristicValueHierarchy[], presetAssocs: ICharacteristicValueHierarchyValueGroupAssociation[]) {
		return this._httpClient.post(`${this._baseUrl}/picklists`)
			.body({
				picklist,
				charValues,
				hierarchies,
				presetAssocs
			})
			.fetch<string>();
	}

	updatePicklist(picklist: ICharacteristicValueSource, charValues: ICharacteristicValue[], hierarchies: ICharacteristicValueHierarchy[], presetAssocs: ICharacteristicValueHierarchyValueGroupAssociation[]) {
		return this._httpClient.put(`${this._baseUrl}/picklists`)
			.body({
				picklist,
				charValues,
				hierarchies,
				presetAssocs
			})
			.fetch<void>();
	}

	createPreset(picklistId: string, preset: ICharacteristicValueGroup) {
		return this._httpClient.post(`${this._baseUrl}/picklists/${picklistId}/presets`)
			.body(preset)
			.fetch<string>();
	}

	updatePreset(picklistId: string, preset: ICharacteristicValueGroup) {
		return this._httpClient.put(`${this._baseUrl}/picklists/${picklistId}/presets/${preset.charValueGroupID}`)
			.body(preset)
			.fetch<string>();
	}

	deletePreset(picklistId: string, presetId: string) {
		return this._httpClient.delete(`${this._baseUrl}/picklists/${picklistId}/presets/${presetId}`)
			.fetch<void>();
	}

	deletePicklist(charValueSourceId: string) {
		return this._httpClient
			.delete(`${this._baseUrl}/picklists/${charValueSourceId}/delete`)
			.fetch<void>();
	}

	updatePresetsSequenceOrder(charValueSourceId: string, charValueGroupIds: string[]) {
		return this._httpClient
			.put(`${this._baseUrl}/picklists/${charValueSourceId}/update-presets-sequence-order`)
			.body({ charValueGroupIds })
			.fetch<void>();
	}

	exportListOfValuesHierarchyToCsv(model: IExportLovHierarchyModel) {
		return this._httpClient
			.post(`${this._baseUrl}/picklists/${model.charValueSourceId}/export-lov-hierarchy`)
			.body(model)
			.fetch<string>();
	}

	createAdvancedPicklistRecords(templateId: number, picklistSourceId: string): Observable<string> {
		this._httpCache.remove(this.getPicklistKey(picklistSourceId));
		this._httpCache.remove(this.getPicklistMetaDataKey(picklistSourceId));
		return this._httpClient
			.post(`${this._baseUrl}/create-advanced-picklist`)
			.body({ templateId, picklistSourceId })
			.fetch<string>();
	}

	getAdvancedPicklists(charValueSetId: string, model: ISearchRequestModel) {
		return this._httpClient
			.post(`${this._baseUrl}/advanced-picklists/${charValueSetId}`)
			.body({ ...model })
			.fetch<IPicklistResponse>();
	}

	setupAdvancedPicklists(picklistSourceId: string, autoMap: boolean) {
		this._httpCache.remove(this.getPicklistKey(picklistSourceId));
		this._httpCache.remove(this.getPicklistMetaDataKey(picklistSourceId));
		return this._httpClient
			.post(`${this._baseUrl}/advanced-picklists/${picklistSourceId}/setup?autoMap=${autoMap}`)
			.fetch<string>();
	}
}
